import React, { useEffect, useState } from 'react';
import AppHeader from '../components/AppHeader';
import Swal from 'sweetalert2';
import {
    getClientList,
    createClient,
    updateClient,
    deleteClient,
    getLogisticsMasterList,
    createLogisticsMaster,
    updateLogisticsMaster,
    deleteLogisticsMaster,
    createClientMapping,
    getClientMappingByLogistics,
    deleteClientMapping,
    getClientMappingList,
    regenerateLogisticsPassword
} from '../services/logisticsService';
import Select from 'react-select';


function MastersPage() {
    const [activeTab, setActiveTab] = useState('client');

    const [showClientModal, setShowClientModal] = useState(false);

    const [clientLoading, setClientLoading] = useState(false);
    const [clientSubmitting, setClientSubmitting] = useState(false);
    const [isClientEditMode, setIsClientEditMode] = useState(false);
    const [editingClientId, setEditingClientId] = useState(null);

    const [showLogisticsModal, setShowLogisticsModal] = useState(false);

    const [logisticsForm, setLogisticsForm] = useState({
        name: '',
        mobile: '',
        code: '',
        is_active: true,
    });

    const [logisticsList, setLogisticsList] = useState([]);
    const [logisticsLoading, setLogisticsLoading] = useState(false);
    const [logisticsSubmitting, setLogisticsSubmitting] = useState(false);

    const [isLogisticsEditMode, setIsLogisticsEditMode] = useState(false);
    const [editingLogisticsId, setEditingLogisticsId] = useState(null);

    const [clientForm, setClientForm] = useState({
        client_name: '',
        client_code: '',
        is_active: true,
    });

    const [clientList, setClientList] = useState([]);

    const handleClientChange = (e) => {
        const { name, value, type, checked } = e.target;

        setClientForm((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const [mappingForm, setMappingForm] = useState({
        logisticsId: '',
        clientId: '',
    });

    const [mappingList, setMappingList] = useState([]);
    const [mappingLoading, setMappingLoading] = useState(false);
    const [mappingSubmitting, setMappingSubmitting] = useState(false);
    const [mappingSearch, setMappingSearch] = useState('');

    const openClientModal = () => {
        setClientForm({
            client_name: '',
            client_code: '',
            is_active: true,
        });
        setEditingClientId(null);
        setIsClientEditMode(false);
        setShowClientModal(true);
    };

    const closeClientModal = () => {
        setShowClientModal(false);
        setEditingClientId(null);
        setIsClientEditMode(false);
    };

    const handleEditClient = (client) => {
        setClientForm({
            client_name: client.client_name || '',
            client_code: client.client_code || '',
            is_active: client.is_active ?? true,
        });

        setEditingClientId(client.id);
        setIsClientEditMode(true);
        setShowClientModal(true);
    };

    const fetchClientList = async () => {
        try {
            setClientLoading(true);

            const response = await getClientList();
            const result = response.data;

            if (result?.status === 1) {
                setClientList(result.data || []);
            } else {
                setClientList([]);
                Swal.fire({
                    icon: 'error',
                    title: 'Failed',
                    text: result?.message || 'Unable to fetch client list',
                });
            }
        } catch (error) {
            setClientList([]);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text:
                    error?.response?.data?.message ||
                    'Something went wrong while fetching client list',
            });
        } finally {
            setClientLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'client') {
            fetchClientList();
        }
    }, [activeTab]);

    const handleSaveClient = async () => {
        if (!clientForm.client_name.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Validation',
                text: 'Client name is required',
            });
            return;
        }

        try {
            setClientSubmitting(true);

            const payload = {
                client_name: clientForm.client_name.trim(),
                client_code: clientForm.client_code.trim() || null,
                is_active: clientForm.is_active,
            };

            let response;

            if (isClientEditMode && editingClientId) {
                response = await updateClient(editingClientId, payload);
            } else {
                response = await createClient(payload);
            }

            const result = response.data;

            if (result?.status === 1) {
                closeClientModal();
                await fetchClientList();

                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: isClientEditMode
                        ? 'Client updated successfully'
                        : 'Client created successfully',
                    timer: 1600,
                    showConfirmButton: false,
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Failed',
                    text:
                        result?.message ||
                        (isClientEditMode
                            ? 'Unable to update client'
                            : 'Unable to create client'),
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text:
                    error?.response?.data?.message ||
                    (isClientEditMode
                        ? 'Something went wrong while updating client'
                        : 'Something went wrong while creating client'),
            });
        } finally {
            setClientSubmitting(false);
        }
    };

    const handleDeleteClient = async (client) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: 'Delete Client?',
            text: `Are you sure you want to delete ${client.client_name}?`,
            showCancelButton: true,
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#dc2626',
        });

        if (!result.isConfirmed) return;

        try {
            const response = await deleteClient(client.id);
            const apiResult = response.data;

            if (apiResult?.status === 1) {
                await fetchClientList();

                Swal.fire({
                    icon: 'success',
                    title: 'Deleted',
                    text: 'Client deleted successfully',
                    timer: 1500,
                    showConfirmButton: false,
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Failed',
                    text: apiResult?.message || 'Unable to delete client',
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text:
                    error?.response?.data?.message ||
                    'Something went wrong while deleting client',
            });
        }
    };

    const handleLogisticsChange = (e) => {
        const { name, value, type, checked } = e.target;

        setLogisticsForm((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const fetchLogisticsList = async () => {
        try {
            setLogisticsLoading(true);

            const response = await getLogisticsMasterList();
            const result = response.data;

            if (result?.status === 1) {
                setLogisticsList(result.data || []);
            } else {
                setLogisticsList([]);
                Swal.fire({
                    icon: 'error',
                    title: 'Failed',
                    text: result?.message || 'Unable to fetch logistics list',
                });
            }
        } catch (error) {
            setLogisticsList([]);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text:
                    error?.response?.data?.message ||
                    'Something went wrong while fetching logistics list',
            });
        } finally {
            setLogisticsLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'client') {
            fetchClientList();
        }

        if (activeTab === 'logistics') {
            fetchLogisticsList();
        }
    }, [activeTab]);


    const openLogisticsModal = () => {
        setLogisticsForm({
            name: '',
            mobile: '',
            code: '',
            is_active: true,
        });
        setEditingLogisticsId(null);
        setIsLogisticsEditMode(false);
        setShowLogisticsModal(true);
    };

    const closeLogisticsModal = () => {
        setShowLogisticsModal(false);
        setEditingLogisticsId(null);
        setIsLogisticsEditMode(false);
    };


    const handleEditLogistics = (logistics) => {
        setLogisticsForm({
            name: logistics.name || '',
            mobile: logistics.mobile || '',
            code: logistics.code || '',
            is_active: logistics.is_active ?? true,
        });

        setEditingLogisticsId(logistics.id);
        setIsLogisticsEditMode(true);
        setShowLogisticsModal(true);
    };

    const handleSaveLogistics = async () => {
        if (!logisticsForm.name.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Validation',
                text: 'Logistics name is required',
            });
            return;
        }

        if (!logisticsForm.mobile.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Validation',
                text: 'Mobile number is required',
            });
            return;
        }

        try {
            setLogisticsSubmitting(true);

            const payload = {
                name: logisticsForm.name.trim(),
                mobile: logisticsForm.mobile.trim(),
                code: logisticsForm.code.trim() || null,
                is_active: logisticsForm.is_active,
            };

            let response;

            if (isLogisticsEditMode && editingLogisticsId) {
                response = await updateLogisticsMaster(editingLogisticsId, payload);
            } else {
                response = await createLogisticsMaster(payload);
            }

            const result = response.data;

            if (result?.status === 1) {
                closeLogisticsModal();
                await fetchLogisticsList();

                if (isLogisticsEditMode) {
                    await Swal.fire({
                        icon: 'success',
                        title: 'Success',
                        text: 'Logistics updated successfully',
                        timer: 1600,
                        showConfirmButton: false,
                    });
                } else {
                    const loginData = result?.data?.login_credentials;

                    await Swal.fire({
                        icon: 'success',
                        title: 'Logistics Created',
                        html: `
            <div style="text-align:left; line-height:1.8;">
              <div><strong>Name:</strong> ${result?.data?.logistics?.name || '-'}</div>
              <div><strong>Mobile:</strong> ${loginData?.mobile_number || '-'}</div>
              <div><strong>Password:</strong> ${loginData?.password || '-'}</div>
            </div>
          `,
                        confirmButtonText: 'OK',
                    });
                }
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Failed',
                    text:
                        result?.message ||
                        (isLogisticsEditMode
                            ? 'Unable to update logistics'
                            : 'Unable to create logistics'),
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text:
                    error?.response?.data?.message ||
                    (isLogisticsEditMode
                        ? 'Something went wrong while updating logistics'
                        : 'Something went wrong while creating logistics'),
            });
        } finally {
            setLogisticsSubmitting(false);
        }
    };

    const handleDeleteLogistics = async (logistics) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: 'Delete Logistics?',
            text: `Are you sure you want to delete ${logistics.name}?`,
            showCancelButton: true,
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#dc2626',
        });

        if (!result.isConfirmed) return;

        try {
            const response = await deleteLogisticsMaster(logistics.id);
            const apiResult = response.data;

            if (apiResult?.status === 1) {
                await fetchLogisticsList();

                Swal.fire({
                    icon: 'success',
                    title: 'Deleted',
                    text: 'Logistics deleted successfully',
                    timer: 1500,
                    showConfirmButton: false,
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Failed',
                    text: apiResult?.message || 'Unable to delete logistics',
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text:
                    error?.response?.data?.message ||
                    'Something went wrong while deleting logistics',
            });
        }
    };

    const handleMappingChange = (e) => {
        const { name, value } = e.target;

        setMappingForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const fetchMappingList = async (logisticsId) => {
        if (!logisticsId) {
            setMappingList([]);
            return;
        }

        try {
            setMappingLoading(true);

            const response = await getClientMappingByLogistics(logisticsId);
            const result = response.data;

            if (result?.status === 1) {
                setMappingList(result.data || []);
            } else {
                setMappingList([]);
                Swal.fire({
                    icon: 'error',
                    title: 'Failed',
                    text: result?.message || 'Unable to fetch mapping list',
                });
            }
        } catch (error) {
            setMappingList([]);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text:
                    error?.response?.data?.message ||
                    'Something went wrong while fetching mapped clients',
            });
        } finally {
            setMappingLoading(false);
        }
    };

    const handleSaveMapping = async () => {
        if (!mappingForm.logisticsId) {
            Swal.fire({
                icon: 'warning',
                title: 'Validation',
                text: 'Please select a logistics person',
            });
            return;
        }

        if (!mappingForm.clientId) {
            Swal.fire({
                icon: 'warning',
                title: 'Validation',
                text: 'Please select a client',
            });
            return;
        }

        try {
            setMappingSubmitting(true);

            const payload = {
                logisticsId: Number(mappingForm.logisticsId),
                clientId: Number(mappingForm.clientId),
            };

            const response = await createClientMapping(payload);
            const result = response.data;

            if (result?.status === 1) {
                setMappingForm((prev) => ({
                    ...prev,
                    clientId: '',
                }));

                await fetchMappingList(mappingForm.logisticsId);

                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: 'Client mapped successfully',
                    timer: 1500,
                    showConfirmButton: false,
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Failed',
                    text: result?.message || 'Unable to create mapping',
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text:
                    error?.response?.data?.message ||
                    'Something went wrong while creating mapping',
            });
        } finally {
            setMappingSubmitting(false);
        }
    };

    const handleDeleteMapping = async (item) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: 'Remove Mapping?',
            text: 'Are you sure you want to remove this client mapping?',
            showCancelButton: true,
            confirmButtonText: 'Yes, Remove',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#dc2626',
        });

        if (!result.isConfirmed) return;

        try {
            const response = await deleteClientMapping(item.id);
            const apiResult = response.data;

            if (apiResult?.status === 1) {
                await fetchMappingList(mappingForm.logisticsId);

                Swal.fire({
                    icon: 'success',
                    title: 'Removed',
                    text: 'Mapping removed successfully',
                    timer: 1500,
                    showConfirmButton: false,
                });
                fetchAllMappings();
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Failed',
                    text: apiResult?.message || 'Unable to remove mapping',
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text:
                    error?.response?.data?.message ||
                    'Something went wrong while removing mapping',
            });
        }
    };

    const fetchAllMappings = async () => {
        try {
            setMappingLoading(true);

            const response = await getClientMappingList();
            const result = response.data;

            if (result?.status === 1) {
                setMappingList(result.data || []);
            } else {
                setMappingList([]);
            }
        } catch (error) {
            setMappingList([]);
        } finally {
            setMappingLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'client') {
            fetchClientList();
        }

        if (activeTab === 'logistics') {
            fetchLogisticsList();
        }

        if (activeTab === 'mapping') {
            fetchLogisticsList();
            fetchClientList();
        }
    }, [activeTab]);

    useEffect(() => {
        if (activeTab === 'mapping') {
            fetchLogisticsList();
            fetchClientList();
            fetchAllMappings();
        }
    }, [activeTab]);

    const groupedMappings = mappingList.reduce((acc, item) => {
        const logisticsId = item.logistics_id?.id;

        if (!logisticsId) return acc;

        if (!acc[logisticsId]) {
            acc[logisticsId] = {
                logistics: item.logistics_id,
                clients: [],
            };
        }

        acc[logisticsId].clients.push({
            id: item.id,
            client: item.client_id,
        });

        return acc;
    }, {});

    const filteredGroupedMappings = Object.values(groupedMappings).filter((group) => {
        const logisticsName = group.logistics?.name?.toLowerCase() || '';
        const logisticsCode = group.logistics?.code?.toLowerCase() || '';
        const searchText = mappingSearch.toLowerCase().trim();

        if (!searchText) return true;

        return (
            logisticsName.includes(searchText) ||
            logisticsCode.includes(searchText)
        );
    });

    const handleRegeneratePassword = async (item) => {
        const confirm = await Swal.fire({
            icon: 'warning',
            title: 'Regenerate Password',
            html: `
    <div style="
      margin-top:10px;
      background:#fff7ed;
      border:1px solid #fed7aa;
      border-radius:12px;
      padding:16px;
      text-align:left;
      font-size:14px;
    ">

      <div style="
        margin-bottom:10px;
        font-weight:600;
        color:#9a3412;
      ">
        Are you sure you want to generate a new password?
      </div>

      <div style="
        display:flex;
        justify-content:space-between;
        margin-bottom:8px;
      ">
        <span style="color:#64748b;">Logistics Name</span>
        <span style="font-weight:600;color:#1e293b;">
          ${item.name}
        </span>
      </div>

      <div style="
        font-size:12px;
        color:#b45309;
        margin-top:10px;
      ">
        The previous password will be replaced and the user must use the new password to login.
      </div>

    </div>
  `,
            showCancelButton: true,
            confirmButtonText: 'Generate Password',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#f59e0b',
            cancelButtonColor: '#64748b',
        });

        if (!confirm.isConfirmed) return;

        try {
            const response = await regenerateLogisticsPassword(item.id);
            const result = response.data;

            if (result?.status === 1) {
                Swal.fire({
                    icon: 'success',
                    title: 'New Login Credentials',
                    html: `
    <div style="
      margin-top:12px;
      background:#f8fafc;
      border:1px solid #e2e8f0;
      border-radius:14px;
      padding:18px;
      text-align:left;
      font-family:system-ui;
    ">

      <div style="margin-bottom:12px;font-size:14px;">
        <div style="color:#64748b;">Name</div>
        <div style="font-weight:600;color:#1e293b;">
          ${result.data.logistics_name}
        </div>
      </div>

      <div style="margin-bottom:14px;font-size:14px;">
        <div style="color:#64748b;">Mobile</div>
        <div style="
          display:flex;
          justify-content:space-between;
          align-items:center;
          margin-top:4px;
        ">
          <span style="font-weight:600;color:#1e293b;">
            ${result.data.mobile_number}
          </span>

          <button
            onclick="navigator.clipboard.writeText('${result.data.mobile_number}')"
            style="
              background:#2563eb;
              border:none;
              color:white;
              padding:4px 10px;
              border-radius:6px;
              cursor:pointer;
              font-size:12px;
            "
          >
            Copy
          </button>
        </div>
      </div>

      <div style="font-size:14px;">
        <div style="color:#64748b;">Password</div>
        <div style="
          display:flex;
          justify-content:space-between;
          align-items:center;
          margin-top:4px;
        ">
          <span style="
            background:#e2e8f0;
            padding:5px 10px;
            border-radius:6px;
            font-weight:700;
            font-family:monospace;
          ">
            ${result.data.password}
          </span>

          <button
            onclick="navigator.clipboard.writeText('${result.data.password}')"
            style="
              background:#16a34a;
              border:none;
              color:white;
              padding:4px 10px;
              border-radius:6px;
              cursor:pointer;
              font-size:12px;
            "
          >
            Copy
          </button>
        </div>
      </div>

      <div style="
        margin-top:14px;
        font-size:12px;
        color:#f59e0b;
        text-align:center;
      ">
        Please share these credentials securely with the logistics user.
      </div>

    </div>
  `,
                    confirmButtonText: 'Done',
                    confirmButtonColor: '#2563eb'
                });
            } else {
                Swal.fire('Error', result.message, 'error');
            }
        } catch (error) {
            Swal.fire('Error', 'Failed to regenerate password', 'error');
        }
    };

    // 🔹 Logistics Options
    const logisticsOptions = [
        { value: '', label: 'Select logistics' },
        ...logisticsList.map((item) => ({
            value: item.id,
            label: `${item.name}${item.code ? ` (${item.code})` : ''}`,
        })),
    ];

    // 🔹 Client Options
    const clientOptions = [
        { value: '', label: 'Select client' },
        ...clientList.map((item) => ({
            value: item.id,
            label: `${item.client_name}${item.client_code ? ` (${item.client_code})` : ''}`,
        })),
    ];



    return (
        <div style={pageStyle}>
            <AppHeader />

            <div style={containerStyle}>
                <div style={topBarStyle}>
                    <div>
                        <h1 style={titleStyle}>Master Management</h1>
                        <p style={subTitleStyle}>
                            Manage clients and logistics from one place
                        </p>
                    </div>
                </div>

                <div style={tabWrapperStyle}>
                    <button
                        onClick={() => setActiveTab('client')}
                        style={{
                            ...tabButtonStyle,
                            ...(activeTab === 'client' ? activeTabStyle : {}),
                        }}
                    >
                        Client Master
                    </button>

                    <button
                        onClick={() => setActiveTab('logistics')}
                        style={{
                            ...tabButtonStyle,
                            ...(activeTab === 'logistics' ? activeTabStyle : {}),
                        }}
                    >
                        Logistics Master
                    </button>

                    <button
                        onClick={() => setActiveTab('mapping')}
                        style={{
                            ...tabButtonStyle,
                            ...(activeTab === 'mapping' ? activeTabStyle : {}),
                        }}
                    >
                        Client Mapping
                    </button>
                </div>

                <div style={contentCardStyle}>
                    {activeTab === 'client' && (
                        <div>
                            <div style={sectionHeaderStyle}>
                                <div>
                                    <h2 style={sectionTitleStyle}>Client Master</h2>
                                    <p style={sectionSubTextStyle}>
                                        Add and manage diagnostic clients
                                    </p>
                                </div>

                                <button style={primaryButtonStyle} onClick={openClientModal}>
                                    + Add Client
                                </button>
                            </div>

                            <div style={tableWrapperStyle}>
                                <table style={tableStyle}>
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>Client Name</th>
                                            <th style={thStyle}>Client Code</th>
                                            <th style={thStyle}>Status</th>
                                            <th style={thStyle}>Action</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {clientLoading ? (
                                            <tr>
                                                <td colSpan="4" style={emptyTdStyle}>
                                                    Loading clients...
                                                </td>
                                            </tr>
                                        ) : clientList.length === 0 ? (
                                            <tr>
                                                <td colSpan="4" style={emptyTdStyle}>
                                                    No client records found
                                                </td>
                                            </tr>
                                        ) : (
                                            clientList.map((item) => (
                                                <tr key={item.id}>
                                                    <td style={tdStyle}>{item.client_name}</td>
                                                    <td style={tdStyle}>{item.client_code || '-'}</td>
                                                    <td style={tdStyle}>
                                                        <span
                                                            style={{
                                                                ...statusBadgeStyle,
                                                                background: item.is_active ? '#dcfce7' : '#fee2e2',
                                                                color: item.is_active ? '#166534' : '#991b1b',
                                                            }}
                                                        >
                                                            {item.is_active ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                    <td style={tdStyle}>
                                                        <button
                                                            style={smallEditButtonStyle}
                                                            onClick={() => handleEditClient(item)}
                                                        >
                                                            Edit
                                                        </button>

                                                        <button
                                                            style={smallDeleteButtonStyle}
                                                            onClick={() => handleDeleteClient(item)}
                                                        >
                                                            Delete
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {activeTab === 'logistics' && (
                        <div>
                            <div style={sectionHeaderStyle}>
                                <div>
                                    <h2 style={sectionTitleStyle}>Logistics Master</h2>
                                    <p style={sectionSubTextStyle}>
                                        Add and manage logistics users
                                    </p>
                                </div>

                                <button style={primaryButtonStyle} onClick={openLogisticsModal}>
                                    + Add Logistics
                                </button>
                            </div>

                            <div style={tableWrapperStyle}>
                                <table style={tableStyle}>
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>Name</th>
                                            <th style={thStyle}>Mobile</th>
                                            <th style={thStyle}>Code</th>
                                            <th style={thStyle}>Status</th>
                                            <th style={thStyle}>Action</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {logisticsLoading ? (
                                            <tr>
                                                <td colSpan="5" style={emptyTdStyle}>
                                                    Loading logistics...
                                                </td>
                                            </tr>
                                        ) : logisticsList.length === 0 ? (
                                            <tr>
                                                <td colSpan="5" style={emptyTdStyle}>
                                                    No logistics records found
                                                </td>
                                            </tr>
                                        ) : (
                                            logisticsList.map((item) => (
                                                <tr key={item.id}>
                                                    <td style={tdStyle}>{item.name}</td>
                                                    <td style={tdStyle}>{item.mobile || '-'}</td>
                                                    <td style={tdStyle}>{item.code || '-'}</td>
                                                    <td style={tdStyle}>
                                                        <span
                                                            style={{
                                                                ...statusBadgeStyle,
                                                                background: item.is_active ? '#dcfce7' : '#fee2e2',
                                                                color: item.is_active ? '#166534' : '#991b1b',
                                                            }}
                                                        >
                                                            {item.is_active ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                    <td style={tdStyle}>
                                                        <button
                                                            style={smallEditButtonStyle}
                                                            onClick={() => handleEditLogistics(item)}
                                                        >
                                                            Edit
                                                        </button>

                                                        <button
                                                            style={smallDeleteButtonStyle}
                                                            onClick={() => handleDeleteLogistics(item)}
                                                        >
                                                            Delete
                                                        </button>
                                                        <button
                                                            style={smallActionButtonStyle}
                                                            onClick={() => handleRegeneratePassword(item)}
                                                        >
                                                            Reset Password
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {activeTab === 'mapping' && (
                        <div>
                            <div style={sectionHeaderStyle}>
                                <div>
                                    <h2 style={sectionTitleStyle}>Client Mapping</h2>
                                    <p style={sectionSubTextStyle}>
                                        Assign clients to logistics personnel
                                    </p>
                                </div>
                            </div>

                            <div style={mappingCardStyle}>
                                <div style={mappingGridStyle}>
                                    {/* Logistics Dropdown */}
                                    <div style={formGroupStyle}>
                                        <label style={labelStyle}>Select Logistics</label>

                                        <Select
                                            options={logisticsOptions}
                                            value={
                                                logisticsOptions.find(
                                                    (opt) => String(opt.value) === String(mappingForm.logisticsId)
                                                ) || null
                                            }
                                            onChange={(selectedOption) => {
                                                handleMappingChange({
                                                    target: {
                                                        name: 'logisticsId',
                                                        value: selectedOption?.value || '',
                                                    },
                                                });
                                            }}
                                            placeholder="Search logistics..."
                                            isSearchable
                                            styles={customStyles}
                                        />
                                    </div>

                                    {/* Client Dropdown */}
                                    <div style={formGroupStyle}>
                                        <label style={labelStyle}>Select Client</label>

                                        <Select
                                            options={clientOptions}
                                            value={
                                                clientOptions.find(
                                                    (opt) => String(opt.value) === String(mappingForm.clientId)
                                                ) || null
                                            }
                                            onChange={(selectedOption) => {
                                                handleMappingChange({
                                                    target: {
                                                        name: 'clientId',
                                                        value: selectedOption?.value || '',
                                                    },
                                                });
                                            }}
                                            placeholder="Search client..."
                                            isSearchable
                                            styles={customStyles}
                                        />
                                    </div>

                                    <div style={mappingButtonWrapStyle}>
                                        <button
                                            style={{
                                                ...primaryButtonStyle,
                                                width: '100%',
                                                opacity: mappingSubmitting ? 0.7 : 1,
                                                cursor: mappingSubmitting ? 'not-allowed' : 'pointer',
                                            }}
                                            onClick={handleSaveMapping}
                                            disabled={mappingSubmitting}
                                        >
                                            {mappingSubmitting ? 'Mapping...' : 'Map Client'}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div style={mappingSearchWrapperStyle}>
                                <input
                                    type="text"
                                    value={mappingSearch}
                                    onChange={(e) => setMappingSearch(e.target.value)}
                                    placeholder="Search logistics by name or code"
                                    style={mappingSearchInputStyle}
                                />

                                <button onClick={fetchAllMappings} style={smallActionButtonStyle}>
                                    Refresh
                                </button>
                            </div>

                            <div style={mappingListWrapperStyle}>
                                {mappingLoading ? (
                                    <div style={emptyStateStyle}>Loading mapped clients...</div>
                                ) : Object.keys(groupedMappings).length === 0 ? (
                                    <div style={emptyStateStyle}>No mapped clients found</div>
                                ) : (
                                    filteredGroupedMappings.map((group, index) => (
                                        <div key={index} style={mappingGroupCardStyle}>
                                            <div style={mappingGroupHeaderStyle}>
                                                <div>
                                                    <h3 style={mappingGroupTitleStyle}>
                                                        {group.logistics?.name || '-'}
                                                    </h3>
                                                    <p style={mappingGroupSubTitleStyle}>
                                                        Code: {group.logistics?.code || '-'} • {group.clients.length} Client(s) Mapped
                                                    </p>
                                                </div>
                                            </div>

                                            <div style={mappedClientListStyle}>
                                                {group.clients.map((item) => (
                                                    <div className='adminPageFlexWrap' key={item.id} style={mappedClientRowStyle}>
                                                        <div>
                                                            <div style={mappedClientNameStyle}>
                                                                {item.client?.client_name || '-'}
                                                            </div>
                                                            <div style={mappedClientCodeStyle}>
                                                                {item.client?.client_code || '-'}
                                                            </div>
                                                        </div>

                                                        <button
                                                            style={smallDeleteButtonStyle}
                                                            onClick={() => handleDeleteMapping({ id: item.id })}
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {showClientModal && (
                <div style={modalOverlayStyle}>
                    <div style={modalStyle}>
                        <div style={modalHeaderStyle}>
                            <h3 style={modalTitleStyle}>
                                {isClientEditMode ? 'Edit Client' : 'Add Client'}
                            </h3>
                            <button style={modalCloseStyle} onClick={closeClientModal}>
                                ✕
                            </button>
                        </div>

                        <div style={formGroupStyle}>
                            <label style={labelStyle}>Client Name</label>
                            <input
                                type="text"
                                name="client_name"
                                value={clientForm.client_name}
                                onChange={handleClientChange}
                                style={inputStyle}
                                placeholder="Enter client name"
                            />
                        </div>

                        <div style={formGroupStyle}>
                            <label style={labelStyle}>Client Code</label>
                            <input
                                type="text"
                                name="client_code"
                                value={clientForm.client_code}
                                onChange={handleClientChange}
                                style={inputStyle}
                                placeholder="Enter client code"
                            />
                        </div>

                        <div style={checkboxRowStyle}>
                            <input
                                type="checkbox"
                                name="is_active"
                                checked={clientForm.is_active}
                                onChange={handleClientChange}
                            />
                            <label style={checkboxLabelStyle}>Active</label>
                        </div>

                        <div style={modalFooterStyle}>
                            <button style={secondaryButtonStyle} onClick={closeClientModal}>
                                Cancel
                            </button>
                            <button
                                style={{
                                    ...primaryButtonStyle,
                                    opacity: clientSubmitting ? 0.7 : 1,
                                    cursor: clientSubmitting ? 'not-allowed' : 'pointer',
                                }}
                                onClick={handleSaveClient}
                                disabled={clientSubmitting}
                            >
                                {clientSubmitting
                                    ? isClientEditMode
                                        ? 'Updating...'
                                        : 'Saving...'
                                    : isClientEditMode
                                        ? 'Update Client'
                                        : 'Save Client'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showLogisticsModal && (
                <div style={modalOverlayStyle}>
                    <div style={modalStyle}>
                        <div style={modalHeaderStyle}>
                            <h3 style={modalTitleStyle}>Add Logistics</h3>
                            <button style={modalCloseStyle} onClick={closeLogisticsModal}>
                                ✕
                            </button>
                        </div>

                        <div style={formGroupStyle}>
                            <label style={labelStyle}>Name</label>
                            <input
                                type="text"
                                name="name"
                                value={logisticsForm.name}
                                onChange={handleLogisticsChange}
                                style={inputStyle}
                                placeholder="Enter logistics name"
                            />
                        </div>

                        <div style={formGroupStyle}>
                            <label style={labelStyle}>Mobile Number</label>
                            <input
                                type="text"
                                name="mobile"
                                value={logisticsForm.mobile}
                                onChange={handleLogisticsChange}
                                style={inputStyle}
                                placeholder="Enter mobile number"
                            />
                        </div>

                        <div style={formGroupStyle}>
                            <label style={labelStyle}>Code</label>
                            <input
                                type="text"
                                name="code"
                                value={logisticsForm.code}
                                onChange={handleLogisticsChange}
                                style={inputStyle}
                                placeholder="Enter logistics code"
                            />
                        </div>

                        <div style={checkboxRowStyle}>
                            <input
                                type="checkbox"
                                name="is_active"
                                checked={logisticsForm.is_active}
                                onChange={handleLogisticsChange}
                            />
                            <label style={checkboxLabelStyle}>Active</label>
                        </div>

                        <div style={modalFooterStyle}>
                            <button style={secondaryButtonStyle} onClick={closeLogisticsModal}>
                                Cancel
                            </button>

                            <button
                                style={{
                                    ...primaryButtonStyle,
                                    opacity: logisticsSubmitting ? 0.7 : 1,
                                    cursor: logisticsSubmitting ? 'not-allowed' : 'pointer',
                                }}
                                onClick={handleSaveLogistics}
                                disabled={logisticsSubmitting}
                            >
                                {logisticsSubmitting
                                    ? isLogisticsEditMode
                                        ? 'Updating...'
                                        : 'Saving...'
                                    : isLogisticsEditMode
                                        ? 'Update Logistics'
                                        : 'Save Logistics'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const customStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: inputStyle.height || '44px',
        borderRadius: inputStyle.borderRadius || '8px',
        border: inputStyle.border || '1px solid #ccc',
        backgroundColor: inputStyle.backgroundColor || '#fff',
        fontSize: inputStyle.fontSize || '14px',
        boxShadow: 'none',
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#888',
    }),
    singleValue: (provided) => ({
        ...provided,
        color: '#000',
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 9999,
    }),
};

const mappingSearchWrapperStyle = {
    marginBottom: '18px',
};

const smallActionButtonStyle = {
    fontFamily: '"Mulish", sans-serif',
    background: '#f59e0b',
    border: 'none',
    color: '#fff',
    padding: '6px 10px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    marginLeft: '6px',
};

const mappingSearchInputStyle = {
    width: '100%',
    maxWidth: '420px',
    height: '46px',
    border: '1px solid #cbd5e1',
    borderRadius: '12px',
    padding: '0 14px',
    fontSize: '14px',
    color: '#0f172a',
    background: '#ffffff',
    boxSizing: 'border-box',
    outline: 'none',
};

const pageStyle = {
    minHeight: '100vh',
    background: '#f8fafc',
};

const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '24px',
};

const topBarStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
};

const titleStyle = {
    margin: 0,
    fontSize: '28px',
    fontWeight: '700',
    color: '#0f172a',
};

const subTitleStyle = {
    margin: '6px 0 0 0',
    fontSize: '14px',
    color: '#64748b',
};

const tabWrapperStyle = {
    display: 'flex',
    gap: '12px',
    marginBottom: '20px',
    flexWrap: 'wrap',
};

const tabButtonStyle = {
    fontFamily: '"Mulish", sans-serif',
    padding: '12px 18px',
    borderRadius: '12px',
    border: '1px solid #cbd5e1',
    background: '#ffffff',
    color: '#334155',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
};

const activeTabStyle = {
    background: '#2563eb',
    color: '#ffffff',
    border: '1px solid #2563eb',
    boxShadow: '0 8px 18px rgba(37, 99, 235, 0.18)',
};

const contentCardStyle = {
    background: '#ffffff',
    borderRadius: '18px',
    padding: '24px',
    boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
    border: '1px solid #e2e8f0',
};

const sectionTitleStyle = {
    margin: '0 0 10px 0',
    fontSize: '22px',
    fontWeight: '700',
    color: '#0f172a',
};

const placeholderTextStyle = {
    margin: 0,
    fontSize: '14px',
    color: '#64748b',
};

const sectionHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    gap: '12px',
    flexWrap: 'wrap',
};

const sectionSubTextStyle = {
    margin: '6px 0 0 0',
    fontSize: '14px',
    color: '#64748b',
};

const primaryButtonStyle = {
    fontFamily: '"Mulish", sans-serif',
    border: 'none',
    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
    color: '#ffffff',
    padding: '12px 18px',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 10px 20px rgba(37, 99, 235, 0.18)',
};

const secondaryButtonStyle = {
    border: '1px solid #cbd5e1',
    background: '#ffffff',
    color: '#334155',
    padding: '12px 18px',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
};

const tableWrapperStyle = {
    width: '100%',
    overflowX: 'auto',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    whiteSpace: 'nowrap',
};

const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '700px',
};

const thStyle = {
    textAlign: 'left',
    background: '#f8fafc',
    padding: '14px 16px',
    fontSize: '14px',
    fontWeight: '700',
    color: '#334155',
    borderBottom: '1px solid #e2e8f0',
};

const tdStyle = {
    padding: '14px 16px',
    fontSize: '14px',
    color: '#334155',
    borderBottom: '1px solid #e2e8f0',
};

const emptyTdStyle = {
    padding: '30px 16px',
    textAlign: 'center',
    color: '#94a3b8',
    fontSize: '14px',
};

const statusBadgeStyle = {
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: '999px',
    fontSize: '12px',
    fontWeight: '700',
};

const smallEditButtonStyle = {
    fontFamily: '"Mulish", sans-serif',
    border: 'none',
    background: '#eff6ff',
    color: '#1d4ed8',
    padding: '8px 12px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    marginRight: '8px',
};

const smallDeleteButtonStyle = {
    fontFamily: '"Mulish", sans-serif',
    border: 'none',
    background: '#fef2f2',
    color: '#dc2626',
    padding: '8px 12px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
};

const modalOverlayStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
};

const modalStyle = {
    width: '100%',
    maxWidth: '520px',
    background: '#ffffff',
    borderRadius: '20px',
    padding: '24px',
    boxShadow: '0 24px 60px rgba(15, 23, 42, 0.18)',
};

const modalHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
};

const modalTitleStyle = {
    margin: 0,
    fontSize: '22px',
    fontWeight: '700',
    color: '#0f172a',
};

const modalCloseStyle = {
    fontFamily: '"Mulish", sans-serif',
    border: 'none',
    background: '#f1f5f9',
    color: '#334155',
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontSize: '16px',
};

const formGroupStyle = {
    // marginTop: '16px',
};

const labelStyle = {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#334155',
};

const inputStyle = {
    width: '100%',
    height: '46px',
    border: '1px solid #cbd5e1',
    borderRadius: '12px',
    padding: '0 14px',
    fontSize: '14px',
    color: '#0f172a',
    boxSizing: 'border-box',
    outline: 'none',
};

const checkboxRowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '20px',
};

const checkboxLabelStyle = {
    fontSize: '14px',
    color: '#334155',
    fontWeight: '600',
};

const modalFooterStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '10px',
};

const mappingCardStyle = {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '20px',
};

const mappingGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: '16px',
    alignItems: 'end',
};

const mappingButtonWrapStyle = {
    display: 'flex',
    alignItems: 'end',
};


const mappingListWrapperStyle = {
    display: 'grid',
    gap: '18px',
    marginTop: '20px',
};

const mappingGroupCardStyle = {
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '18px',
    padding: '20px',
    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.05)',
};

const mappingGroupHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '1px solid #e2e8f0',
};

const mappingGroupTitleStyle = {
    margin: 0,
    fontSize: '20px',
    fontWeight: '700',
    color: '#0f172a',
};

const mappingGroupSubTitleStyle = {
    margin: '6px 0 0 0',
    fontSize: '13px',
    color: '#64748b',
};

const mappedClientListStyle = {
    display: 'grid',
    gap: '12px',
};

const mappedClientRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 16px',
    border: '1px solid #e2e8f0',
    borderRadius: '14px',
    background: '#f8fafc',
    gap: '12px',
};

const mappedClientNameStyle = {
    fontSize: '15px',
    fontWeight: '600',
    color: '#1e293b',
};

const mappedClientCodeStyle = {
    fontSize: '13px',
    color: '#64748b',
    marginTop: '4px',
};

const emptyStateStyle = {
    padding: '30px',
    textAlign: 'center',
    color: '#64748b',
    background: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '16px',
};


export default MastersPage;